/**
 * `DataTable` — batteries-included preset composing the data-table primitives: sort,
 * pagination, infinite scroll, visibility, density, custom render, cross-page selection,
 * the loading/empty/error state machine, and the WAI-ARIA keyboard grid. Every feature is
 * shaped by the primitives' existing state/columns/props inputs, never a boolean-per-feature
 * flag; a consumer that outgrows the preset reaches the live `table` + `virtualizer` via
 * `onTableReady` / render-prop `children`. This file ORCHESTRATES — each concern lives in a
 * co-located hook (`useSelectionColumn`, `useColumnReorder`, `useTableStateChrome`,
 * `useInfiniteFetch`, `useDataTableGridNav`, `useDataTablePropValidation`) and each rendered
 * plane in a co-located component (`DataTableHeaderRow`, `DataTableBodyRow`,
 * `DataTableSummaryBand`); the derivations/state-machine live under `primitives/`.
 */
import { type CSSProperties, type ReactNode, useEffect, useId, useMemo } from 'react'
import { useEvent } from 'react-use-event-hook'
import {
  createRow,
  getPaginationRowModel,
  type OnChangeFn,
  type PaginationState,
  type Row,
  type RowData,
  type RowSelectionState,
  type Table,
  type TableOptions,
  type VisibilityState,
} from '@tanstack/react-table'
import type { Virtualizer } from '@tanstack/react-virtual'

import { cn } from '@gto/ui/cn'

import type { DataTableProps } from './data-table.types'
import { BottomOverflowShadow } from './primitives/bottom-overflow-shadow'
import { DEFAULT_GROW_COLUMN_MIN_WIDTH, readColumnMeta } from './primitives/column-meta'
import {
  DATA_TABLE_HEADER_BODY_GAP_PX,
  DATA_TABLE_HEADER_HEIGHT,
  DATA_TABLE_ROW_GAP_PX,
  DATA_TABLE_ROW_HEIGHT,
  dataTableCardVariants,
  dataTableSummaryLeadingVariants,
  DEFAULT_DATA_TABLE_DENSITY,
  DEFAULT_DATA_TABLE_SURFACE,
  resolveCardRadius,
  resolveChromeBandSurfaces,
  resolveHeaderBodySpacing,
  stickyBandBackgroundStyle,
  SURFACE_COLOR,
} from './primitives/data-table.styles'
import { DataTableBodyRow } from './primitives/data-table-body-row'
import { DataTableHeaderRow } from './primitives/data-table-header-row'
import {
  DataTableSummaryBand,
  type DataTableSummaryBandOffset,
} from './primitives/data-table-summary-band'
import { DATA_TABLE_SUMMARY_ROW_ID } from './primitives/data-table-summary-band.constants'
import { EndOverflowShadow } from './primitives/end-overflow-shadow'
import { PinnedColumnShadows } from './primitives/pinned-column-shadows'
import { getPinnedColumnOffsets, getPinnedGutterWidths } from './primitives/pinning.styles'
import {
  buildScrollSkeletonTile,
  getScrollSkeletonTracks,
  toUnmountedStretches,
} from './primitives/scroll-skeleton'
import { deriveLoadedRowSelection, EMPTY_SELECTION, isRowSelected } from './primitives/selection'
import { type DataTableState, deriveTableState } from './primitives/state-machine'
import { LoadMoreErrorState, TableLiveRegion } from './primitives/states'
import {
  SELECTION_COLUMN_WIDTH_PX,
  TableBody,
  TablePlaceholderRow,
  TableScrollSkeleton,
  TableSelectionHeaderCell,
} from './primitives/table-parts'
import { TableRoot } from './primitives/table-root'
import { useDataTable, type UseDataTableOptions } from './primitives/use-data-table'
import { useDataTablePropValidation } from './primitives/use-data-table-prop-validation'
import { useRowIndexMap } from './primitives/use-row-index-map'
import { resolveVirtualCount } from './primitives/virtual-count'
import { VisibilityMenu } from './primitives/visibility-menu'
import { useColumnReorder } from './use-column-reorder'
import { useDataTableGridNav } from './use-data-table-grid-nav'
import { useHorizontalOverflowShadows } from './use-horizontal-overflow-shadows'
import { useInfiniteFetch } from './use-infinite-fetch'
import { useSelectionColumn } from './use-selection-column'
import { useTableStateChrome } from './use-table-state-chrome'
import { useVerticalOverflowShadow } from './use-vertical-overflow-shadow'

/**
 * Plain generic function (not `FC`) so `TData` infers from `columns`/`data` at the call
 * site; the invariant `Table<TData>` can't be expressed by a non-generic component type.
 */
export const DataTable = <TData extends RowData>(props: DataTableProps<TData>) => {
  const {
    columns,
    data,
    getRowId,
    sorting,
    onSortingChange,
    manualSorting,
    pagination,
    onPaginationChange,
    manualPagination,
    pageCount,
    infinite,
    virtualizeRows,
    showScrollSkeleton = false,
    hideHeader = false,
    density = DEFAULT_DATA_TABLE_DENSITY,
    headerDensity,
    surface = DEFAULT_DATA_TABLE_SURFACE,
    headerSurface,
    headerBodySpacing,
    rowDivider = false,
    embedded = false,
    clipHorizontalOverflow = false,
    hideScrollbars = false,
    columnVisibility,
    onColumnVisibilityChange,
    showColumnVisibilityMenu,
    getColumnLabel,
    enableColumnPinning,
    showPinnedShadows,
    showEndOverflowShadow,
    columnPinning,
    onColumnPinningChange,
    columnSizing,
    onColumnSizingChange,
    enableColumnReordering,
    columnOrder,
    onColumnOrderChange,
    showColumnMenu,
    columnMenuLabels,
    renderCell,
    renderRow,
    getRowProps,
    detailPanel,
    summaryRow,
    summaryRowPlacement = 'bottom',
    toolbar,
    selection,
    rowSelection,
    onRowSelectionChange,
    selectionToolbar,
    query,
    hasAnyData = false,
    stateCopy,
    announcements,
    renderState,
    onTableReady,
    children,
    tableOptions,
    className,
    height,
    maxHeight,
    'aria-label': ariaLabel,
  } = props

  // EVERY consequence of `hideHeader` derives from this one count, so the flag itself is read
  // exactly once. `headerRowCount` numbers the ARIA grid (body rows start at `aria-rowindex`
  // 1 with no header to occupy it); multiplied by the density height it also gives the
  // windowed body's scroll offset past the in-flow sticky header, and the height of the band
  // painted behind the scrollbar gutter — both of which must collapse to 0 together.
  const headerRowCount = hideHeader ? 0 : 1
  // The header's own density axis (see `headerDensity`), defaulted to the body's. Every
  // consumer of the header HEIGHT reads it — the band, the windowed body's scroll offset, and
  // the scrollbar-gutter paint — while `estimateSize` keeps reading `density` alone.
  const activeHeaderDensity = headerDensity ?? density
  const headerHeightPx = headerRowCount * DATA_TABLE_HEADER_HEIGHT[activeHeaderDensity]
  // Defaulted per row model, not at the destructure, so an explicit `'sm'` from a ruled caller
  // stays distinguishable from the absent prop — same reason `headerSurface` is left undefaulted.
  const activeHeaderBodySpacing = resolveHeaderBodySpacing(headerBodySpacing, rowDivider)
  const headerBodyGapPx =
    !hideHeader && activeHeaderBodySpacing === 'sm' ? DATA_TABLE_HEADER_BODY_GAP_PX : 0

  // The `summaryRow` bands, normalised to a list. A TOP-placed stack sits in the scroll flow
  // between the header and the body, so the windowed body starts that much lower. Its height
  // counts every GIVEN band, not only the rendered ones (those are gated on `populated`
  // below): the offset only positions body rows, and a body renders exactly when the bands do.
  const summaryBands =
    summaryRow === undefined ? [] : Array.isArray(summaryRow) ? summaryRow : [summaryRow]
  const isSummaryOnTop = summaryRowPlacement === 'top'
  const topSummaryBlockPx = isSummaryOnTop
    ? summaryBands.length * DATA_TABLE_ROW_HEIGHT[density]
    : 0
  const bodyScrollOffsetPx = headerHeightPx + topSummaryBlockPx + headerBodyGapPx

  // Either bound gives the grid a scroll viewport, so both drive the card's radius.
  const hasBoundedHeight = height !== undefined || maxHeight !== undefined
  const cardRadius = embedded
    ? 'none'
    : resolveCardRadius({
        rowDivider,
        hideHeader,
        hasBoundedHeight,
      })

  const isInfinite = infinite !== undefined
  // The infinite/server tier ALWAYS windows; a client table renders every row in flow UNLESS
  // it opts into `virtualizeRows` (large static set with heavy per-row cells). Both paths share
  // the one virtualizer built on the client path (it also backs the R6 escape hatch); the
  // difference is only whether the body renders the windowed slice or `rows.map`, and whether
  // keyboard nav windows scroll-into-view or treats the whole set as mounted. Client windowing
  // uses the DENSE count model (slot == row), so no server-total/fetch-next signals are involved.
  const isWindowed = isInfinite || virtualizeRows === true

  // Namespaces the panels' DOM ids (`aria-controls` targets) per table instance, so two
  // tables sharing row ids on one page never cross-wire.
  const detailPanelBaseId = useId()

  // Infinite tier: lay out the loaded rows plus a short placeholder LOOKAHEAD, growing as
  // pages land (DataGridPro `onRowsScrollEnd` behavior — a full-total spacer let a fast
  // scroll fill the viewport with placeholder rows). Without `serverTotal` this collapses
  // to the loaded count; client/paged tiers pass no `infinite`, so `useDataTable` keeps its
  // dense `rows.length`. `loadedCount` is the RAW `data.length` (== the index map's
  // `serverOffsetCount`), NOT the deduped count: a sparse SLOT index IS a server offset
  // (the render + fetch-frontier both address slots by offset), so the floor must cover
  // every loaded offset or a loaded row at a high offset gets no slot and is unrenderable.
  // `aria-rowcount` floors on the deduped `logicalCount` instead — a different index space.
  const virtualCount = isInfinite
    ? resolveVirtualCount({
        loadedCount: data?.length ?? 0,
        serverTotal: infinite.serverTotal,
        countModel: 'sparse',
        rowHeightPx: DATA_TABLE_ROW_HEIGHT[density],
      }).count
    : undefined

  // `selection` and the raw `rowSelection` prop are mutually exclusive and share the
  // engine's single `rowSelection` slot. When a `SelectionModel` is given, feed the engine
  // the LOADED per-row selection derived from it so `table.getRow(id).getIsSelected()` agrees
  // with the rendered checkboxes. Derived from `data` + `getRowId` (never read back off the
  // not-yet-built `table`); `allMatching` stays O(loaded), never O(serverTotal). Memoized on
  // `selection?.model` only — a stable-ref model (R3) — so inline labels / a fresh
  // `onSelectionChange` per render don't re-run it.
  const selectionModel = selection?.model
  const engineRowSelection = useMemo<RowSelectionState | undefined>(() => {
    if (selectionModel === undefined) {
      return undefined
    }
    // Loaded row ids exactly as TanStack keys them (`getRowId` or the array-index string).
    const loadedIds = (data ?? []).map((row, index) =>
      getRowId === undefined ? String(index) : getRowId(row, index),
    )

    return deriveLoadedRowSelection(selectionModel, loadedIds)
  }, [selectionModel, data, getRowId])

  // Controlled state for the engine: only defined slices are included so an uncontrolled
  // table keeps TanStack's defaults. Memoized for a stable identity (R3). `selection` (via
  // `engineRowSelection`) wins over the raw `rowSelection` prop for the shared slot.
  const state = useMemo(() => {
    // `engineRowSelection` (derived from `selection`) wins the shared slot; the raw
    // `rowSelection` prop is the fallback; neither given → omit so the engine stays uncontrolled.
    const activeRowSelection = engineRowSelection ?? rowSelection

    return {
      ...(sorting === undefined ? {} : { sorting }),
      ...(pagination === undefined ? {} : { pagination }),
      ...(columnVisibility === undefined ? {} : { columnVisibility }),
      ...(columnPinning === undefined ? {} : { columnPinning }),
      ...(columnSizing === undefined ? {} : { columnSizing }),
      ...(columnOrder === undefined ? {} : { columnOrder }),
      ...(activeRowSelection === undefined ? {} : { rowSelection: activeRowSelection }),
    }
  }, [
    sorting,
    pagination,
    columnVisibility,
    columnPinning,
    columnSizing,
    columnOrder,
    rowSelection,
    engineRowSelection,
  ])

  // `onChange` handlers are event-time only, so `useEvent` is the correct stabilizer
  // (never called during render).
  const handlePaginationChange = useEvent<OnChangeFn<PaginationState>>(
    onPaginationChange ?? (() => undefined),
  )
  const handleVisibilityChange = useEvent<OnChangeFn<VisibilityState>>(
    onColumnVisibilityChange ?? (() => undefined),
  )
  const handleRowSelectionChange = useEvent<OnChangeFn<RowSelectionState>>(
    onRowSelectionChange ?? (() => undefined),
  )

  const mergedTableOptions = useMemo<Partial<TableOptions<TData>>>(() => {
    const merged: Partial<TableOptions<TData>> = {}
    // Server pagination: the engine does NOT slice (consumer drives pages). Client
    // pagination needs the pagination row model to slice locally.
    if (manualPagination === true) {
      merged.manualPagination = true
      merged.pageCount = pageCount
    } else if (pagination !== undefined) {
      merged.getPaginationRowModel = getPaginationRowModel<TData>()
    }
    if (onPaginationChange !== undefined) {
      merged.onPaginationChange = handlePaginationChange
    }
    if (onColumnVisibilityChange !== undefined) {
      merged.onColumnVisibilityChange = handleVisibilityChange
    }
    if (onRowSelectionChange !== undefined) {
      merged.onRowSelectionChange = handleRowSelectionChange
    }

    return { ...merged, ...tableOptions }
  }, [
    manualPagination,
    pageCount,
    pagination,
    onPaginationChange,
    onColumnVisibilityChange,
    onRowSelectionChange,
    handlePaginationChange,
    handleVisibilityChange,
    handleRowSelectionChange,
    tableOptions,
  ])

  // Every tier feeds loaded rows via `data` (client: static array; infinite: flattened
  // loaded pages, with `infinite` supplying the pagination signals). Callers pass
  // reference-stable `data`/`infinite` (R3).
  const preparedColumns = useMemo(
    () =>
      columns.map((column) => {
        const meta = readColumnMeta({ columnDef: column })

        if (meta?.grow !== true) {
          return column
        }

        return {
          ...column,
          meta: {
            ...meta,
            growMinWidth: column.minSize ?? DEFAULT_GROW_COLUMN_MIN_WIDTH,
          },
        }
      }),
    [columns],
  )
  const useDataTableOptions: UseDataTableOptions<TData> = {
    data,
    columns: preparedColumns,
    getRowId,
    state,
    onSortingChange,
    manualSorting,
    // Interactive column-state seams: pass a handler to lift the slice out, omit it and
    // the hook owns it internally so the menu/handle/drag work uncontrolled (see hook doc).
    onColumnPinningChange,
    onColumnSizingChange,
    onColumnOrderChange,
    // Pinning is opt-in (off by default): enable it explicitly, or implicitly by engaging the
    // pinning axis (a `columnPinning` value or an `onColumnPinningChange` handler). Only a plain
    // table with no pinning intent stays pin-less (and therefore menu-less on sort-only columns).
    enableColumnPinning:
      enableColumnPinning ?? (columnPinning !== undefined || onColumnPinningChange !== undefined),
    density,
    // Bordered rows sit contiguous — drop the 1px floating-row slot gap so the windowed
    // slot height stays in lockstep with the row's inline height (see `rowGapPx` doc).
    rowGapPx: rowDivider === false ? DATA_TABLE_ROW_GAP_PX : 0,
    tableOptions: mergedTableOptions,
    // The header is an IN-FLOW sticky row, so the windowed body starts `headerHeightPx` below
    // the scroll top (`0` when headerless — see the derivation above), plus a top-placed
    // summary stack. A consumer can also reserve the compact header-to-body breathing room.
    // `scrollMargin` makes `scrollToIndex` reach the true max (the last rows come fully into
    // view, not a header short); `scrollPaddingStart` lands an aligned row BELOW the sticky
    // chrome, not behind it. The windowed `translateY` below subtracts the same margin back out.
    virtual: {
      // `undefined` on client/paged tiers → dense `rows.length`; infinite passes the sparse
      // (or ceiling-clamped) count for the server total's worth of slots.
      count: virtualCount,
      scrollMargin: bodyScrollOffsetPx,
      scrollPaddingStart: bodyScrollOffsetPx,
    },
  }

  const {
    table,
    virtualizer,
    rows,
    scrollRef,
    density: activeDensity,
    sortingState,
    columnSizingState,
    isAnyColumnResizing,
    columnPinningState,
    columnOrderState,
    columnVisibilityState,
  } = useDataTable<TData>(useDataTableOptions)

  // Prop-contract guards (two fail-loud combinations + five dev warnings). After
  // `useDataTable` because the grow-column check reads engine state.
  useDataTablePropValidation<TData>({
    table,
    isInfinite,
    isWindowed,
    pagination,
    detailPanel,
    selection,
    rowSelection,
    onRowSelectionChange,
    virtualizeRows,
    height,
    maxHeight,
    rowDivider,
    surface,
    headerSurface,
    hasTopPlacedSummaryBand: isSummaryOnTop && summaryBands.length > 0,
  })

  // R6 escape hatch: hand the live `table` + `virtualizer` to a consumer that outgrows the
  // preset. Fired in an effect (after commit) since it's a side effect; both instances are
  // stable per mount, so this fires once and again only if an instance changes.
  const emitTableReady = useEvent(
    (instance: Table<TData>, virtualizerInstance: Virtualizer<HTMLElement, Element>) =>
      onTableReady?.(instance, virtualizerInstance),
  )
  useEffect(
    function emitTableReadyOnMount() {
      emitTableReady(table, virtualizer)
    },
    [emitTableReady, table, virtualizer],
  )

  // The leading selection column (U8) is hand-rendered OUTSIDE the TanStack model, so it
  // never appears in `getVisibleLeafColumns()` but still occupies `SELECTION_COLUMN_WIDTH_PX`
  // of the left edge. Seed the pinned-offset accumulator with that width so a left-pinned
  // DATA column clears it instead of colliding at `left: 0`.
  //
  // MEMOIZED on exactly the state slices the geometry derives from (pin / size / order /
  // visibility): a stable map identity across unrelated renders (a selection toggle, a
  // refetch flag) is what lets the compiler bail out every mounted body row — measured at
  // ~200ms of row re-renders per header-checkbox click before this (FE-836 QA round 13).
  // The slices are reactive VALUES from `useDataTable`, so a real geometry change still
  // rebuilds the map (the old every-render rebuild was the reactivity carrier; the deps
  // now carry it).
  const hasSelectionColumn = selection !== undefined
  const pinnedLeftInset = hasSelectionColumn ? SELECTION_COLUMN_WIDTH_PX : 0
  // BOTH readers below walk the same geometry and take the same `PinnedGeometryMemoKey`
  // (which documents why an argument is needed at all) — they must be invalidated together:
  // a memoized offsets map beside an unmemoized gutter read is how the rail and the cells end
  // up disagreeing about where the pinned group ends. The literal is spelled twice because a
  // shared object would itself be the only dep `exhaustive-deps` could see; the TYPE is what
  // keeps the input list in one place.
  const pinnedOffsets = useMemo(
    () =>
      getPinnedColumnOffsets(table, pinnedLeftInset, {
        columnDefs: preparedColumns,
        columnPinningState,
        columnSizingState,
        columnOrderState,
        columnVisibilityState,
      }),
    [
      table,
      pinnedLeftInset,
      preparedColumns,
      columnPinningState,
      columnSizingState,
      columnOrderState,
      columnVisibilityState,
    ],
  )
  // Sticky width of each pinned group — where the continuous shadow rail sits. `0` on a
  // side with no pins (the rail is then skipped). Shares the selection-column inset with
  // `getPinnedColumnOffsets` so the left rail lands at the group's true right edge.
  //
  // MEMOIZED on the same key, and not merely for the identity: this file is
  // compiler-transformed, and a bare read off the referentially-stable `table` is what the
  // header's frozen sort glyph taught us not to do — the rails, the leading seam lane and
  // the end-overflow shadow all gate on these widths, and the hands header menu offers
  // pin/unpin at runtime.
  const pinnedGutters = useMemo(
    () =>
      getPinnedGutterWidths(table, pinnedLeftInset, {
        columnDefs: preparedColumns,
        columnPinningState,
        columnSizingState,
        columnOrderState,
        columnVisibilityState,
      }),
    [
      table,
      pinnedLeftInset,
      preparedColumns,
      columnPinningState,
      columnSizingState,
      columnOrderState,
      columnVisibilityState,
    ],
  )

  // The U4 index map drives `aria-rowcount`. Build `indexMapRows` and the id lookup in ONE
  // pass over the same `rows` so getRowId is O(1) per row (a per-row `rows.find` would be
  // O(n²) across the body at the ceiling); both memoized on the stable `rows` (R3).
  const { indexMapRows, idByOriginal } = useMemo(() => {
    const originals: TData[] = []
    const idMap = new WeakMap<object, string>()
    for (const row of rows) {
      originals.push(row.original)
      // Row originals are objects; the WeakMap keys on them.
      idMap.set(row.original as object, row.id)
    }

    return { indexMapRows: originals, idByOriginal: idMap }
  }, [rows])
  // Plain memoized closure (NOT `useEvent`): `useRowIndexMap` calls this during render, and
  // `useEvent` callbacks throw when invoked during render. Memoized on `idByOriginal`.
  const indexMapGetRowId = useMemo(
    () => (row: TData) => idByOriginal.get(row as object) ?? '',
    [idByOriginal],
  )
  const indexMap = useRowIndexMap<TData>({
    rows: indexMapRows,
    getRowId: indexMapGetRowId,
    serverTotal: infinite?.serverTotal,
    hasNextPage: infinite?.hasNextPage,
    countModel: isInfinite ? 'sparse' : 'dense',
    headerRowCount,
  })

  // --- U9 loading / empty / error state machine ---
  // One mapping of the React-Query flags + `isFetchingNextPage` + loaded row count to a
  // `TableState`. No `query` (client tier) collapses to Populated ⇄ Empty. `staleRows` is
  // the keepPreviousData signal the selection/focus derivations gate on below.
  const tableState: DataTableState = deriveTableState({
    rowCount: indexMap.logicalCount,
    query,
    isFetchingNextPage: infinite?.isFetchingNextPage,
  })

  // --- pinned summary bands ---
  // Build each aggregate as a REAL engine row WITHOUT putting it in `data`: `createRow` runs the
  // same feature hooks the row model does, so `getVisibleCells()` honours the live visibility /
  // order / pinning and every cell renders through its own column def — while the row model, and
  // with it `aria-rowcount`, the pagination slice, the virtualizer's slots, the fetch frontier
  // and the selection counts, never sees the row at all. `-1` is its row index: it holds no
  // position in the data, and an `accessorFn` that reads the index gets a value that cannot be
  // mistaken for a real one. Rebuilt per render rather than memoized — it is a handful of rows at
  // most, and a long-lived instance would carry TanStack's per-row value cache across a
  // column-def change.
  //
  // Every band shares the ONE summary row id: the id names the plane a consumer's `renderCell` or
  // test tells apart from the body, not a place in the stack, and the rows never enter the row
  // model, where an id would have to be unique. The React key is the band's PLACE in the stack,
  // minted here: a band has no identity of its own — the record it holds has no id, and two
  // bands may hold equal records — and the stack never reorders, so position is the honest key.
  //
  // Gated on `populated` like the body: the loading / empty / error overlays REPLACE the body,
  // and a summary with no rows under it would be a total of nothing. That gate lives in this ONE
  // derivation — the rows' existence IS "the bands render", so no second flag can drift from it.
  const summaryEngineBands =
    tableState.kind === 'populated'
      ? summaryBands.map((band, position) => ({
          band,
          key: `${DATA_TABLE_SUMMARY_ROW_ID}-${position}`,
          row: createRow(table, DATA_TABLE_SUMMARY_ROW_ID, band.row, -1, 0),
        }))
      : []
  // Each band is one body row tall (`TableRow` stamps the density height) and the block is their
  // sum; `0` when none renders, which is what drops its scrollbar-gutter layer.
  const summaryRowHeightPx = DATA_TABLE_ROW_HEIGHT[activeDensity]
  const summaryBandHeightPx = summaryEngineBands.length * summaryRowHeightPx
  /**
   * A band's sticky edge and distance from it. Bottom: lifted by the bands stacked under it.
   * Top: pushed down by the header and the bands stacked above it.
   */
  const summaryBandOffset = (index: number): DataTableSummaryBandOffset =>
    isSummaryOnTop
      ? { top: headerHeightPx + index * summaryRowHeightPx }
      : { bottom: (summaryEngineBands.length - 1 - index) * summaryRowHeightPx }

  const { header: headerBandSurface, summary: summaryBandSurface } = resolveChromeBandSurfaces({
    headerSurface,
    rowDivider,
    surface,
  })

  // Paints each sticky band's colour behind the vertical scrollbar's transparent gutter, as the
  // scroll root's OWN background, so a band reaches the card's edge instead of stopping 6px
  // short of it (see `stickyBandBackgroundStyle` for why nothing else can paint there, and why
  // a band that doesn't render drops its layer — a grid with neither the whole style).
  // A TOP-placed stack still collapses into ONE layer: it continues the header's band downward,
  // so the header layer just grows by the band's height and the end fade, the seam guard and the
  // gutter all read one top block.
  //
  // KNOWN LIMIT, unreachable today: `stickyBandBackgroundStyle` positions a layer by its SLOT
  // (`header` → top, `summary` → bottom), so a top-placed band cannot be given a layer of its
  // own — it would paint at the scrollport's BOTTOM edge, where no band is. Merging is therefore
  // the only expressible option, and under `'ruled'` (blended header, lifted band) it costs the
  // band's 6px gutter strip the header's colour instead of its own. That needs `'ruled'` AND
  // `summaryRowPlacement="top"` together, which no table does — the one top-placed table is not
  // ruled. Giving `StickyBand` an explicit position is the fix whenever one appears; splitting
  // the layers here without it is strictly worse than the mismatch.
  const stickyBands = isSummaryOnTop
    ? { header: { surface: headerBandSurface, heightPx: headerHeightPx + summaryBandHeightPx } }
    : {
        header: { surface: headerBandSurface, heightPx: headerHeightPx },
        summary: { surface: summaryBandSurface, heightPx: summaryBandHeightPx },
      }
  const gutterBandStyle = stickyBandBackgroundStyle(stickyBands)

  // Cross-page selection wiring (U8). `staleRows` applies the keepPreviousData guard so the
  // header never derives select-all from rows about to be replaced.
  const selectionColumn = useSelectionColumn<TData>({
    selection,
    table,
    staleRows: tableState.staleRows,
    logicalCount: indexMap.logicalCount,
    serverTotal: infinite?.serverTotal,
    hasNextPage: infinite?.hasNextPage,
    surface,
    headerSurface: headerBandSurface,
  })

  // --- U6 keyboard grid (R9) ---
  // Make the `role="grid"` below KEYBOARD-OPERABLE: the container (`tabIndex=0`) holds focus
  // and arrows / Home / End / Page move the active cell via `aria-activedescendant` + the
  // cell's `data-active` ring. Handlers act only on the container's OWN key/focus events, so
  // a key on a focused in-cell checkbox never mis-fires the active cell. Space toggles the
  // active row's selection (only with a `selection` model); Enter activates the row.
  const gridNav = useDataTableGridNav<TData>({
    table,
    rows,
    virtualizer,
    isWindowed,
    logicalRowCount: indexMap.logicalCount,
    logicalToVirtual: indexMap.logicalToVirtual,
    virtualToLogical: indexMap.virtualToLogical,
    hasSelectionColumn,
    onToggleRowSelection:
      selectionModel === undefined
        ? undefined
        : (rowId) => selectionColumn.handleToggleRow(rowId, !isRowSelected(selectionModel, rowId)),
  })

  const showMenu =
    showColumnVisibilityMenu ?? table.getAllLeafColumns().some((column) => column.getCanHide())

  // The per-column three-dot header menu. It exists for PINNING and (when enabled) REORDERING —
  // sorting has its own header arrow, so sortability alone does NOT summon the menu (a sort-only
  // column shows just the arrow). Defaults on when any column can pin, or reordering is enabled
  // (the reorder path must render even on a table whose columns can't pin, else a keyboard user
  // loses reorder while a mouse user keeps the drag). A `show*` UI toggle like
  // `showColumnVisibilityMenu`.
  // Per-header gating below still checks each column's capability.
  const reorderEnabled = enableColumnReordering === true
  const showColumnMenuResolved =
    showColumnMenu ??
    (reorderEnabled || table.getAllLeafColumns().some((column) => column.getCanPin()))

  // --- U5 column reordering (opt-in drag-and-drop + the keyboard "Move" items) ---
  // (`reorderEnabled` is computed above, where the header-menu default folds it in.)
  const { columnReorder, getColumnMove } = useColumnReorder<TData>({
    table,
    enabled: reorderEnabled,
  })

  const usesGrowLayout = table
    .getVisibleLeafColumns()
    .some((column) => readColumnMeta(column)?.grow === true)

  // Infinite scroll: the windowed items + the fetch-next-on-end effect.
  const { virtualItems } = useInfiniteFetch({
    infinite,
    loadMoreError: query?.loadMoreError,
    isWindowed,
    virtualizer,
    loadedOffsetCount: indexMap.serverOffsetCount,
  })

  // U9 state chrome: the overlay nodes, the `aria-busy` flag, the `aria-live` messages, and
  // the load-more retry. Where each overlay renders relative to `role="grid"` is the hook's
  // concern.
  const stateChrome = useTableStateChrome({
    tableState,
    query,
    stateCopy,
    announcements,
    renderState,
    hasAnyData,
    rowCount: indexMap.logicalCount,
    selectedCountExact: selectionColumn.derivedSelection.selectedCountExact,
    headerHeightPx: hideHeader ? 0 : headerHeightPx,
  })

  // Continuous pinned-column shadows (MUI pinned-shadow parity): a rail per pinned side is
  // shown ONLY while content is scrolled under that group. Re-measures on horizontal
  // scroll/resize; the key covers content-WIDTH changes a container ResizeObserver misses —
  // total column width (sizing/visibility/order), the pin gutters, populated↔overlay, and the
  // model-external selection column (absent from `getTotalSize`, so a runtime toggle of it
  // would otherwise leave the shadow state stale).
  const overflowShadows = useHorizontalOverflowShadows(
    scrollRef,
    `${table.getTotalSize()}:${pinnedGutters.left}:${pinnedGutters.right}:${tableState.kind}:${String(hasSelectionColumn)}`,
  )

  // Bottom "more rows below" shadow (MUI `data-with-bottom-overlay` parity): shown only while
  // the body overflows vertically and is NOT scrolled to the bottom. Re-measures on vertical
  // scroll/resize; the key covers content-HEIGHT changes a container ResizeObserver misses —
  // row count and density (both change total row height) and populated↔overlay.
  const bottomShadow = useVerticalOverflowShadow(
    scrollRef,
    `${indexMap.logicalCount}:${activeDensity}:${tableState.kind}`,
  )

  // `displayIndex` is the row's position in the DISPLAY-ordered `rows` array — the logical
  // space the U4 map is built from — NOT TanStack's `row.index` (the pre-sort/pre-filter
  // core position, which the sorted/pagination models do NOT renumber). Feeding `row.index`
  // makes a sorted or page-≥2 row announce its original data position, so `aria-rowindex`
  // runs non-monotonic against reading order. Windowed passes `virtualItem.index`;
  // non-windowed the `.map` index — both the display position.
  // Identity-stable windowed slot styles, memoized on the virtualizer's own (internally
  // cached) items array: a render that does not move the window — a selection toggle, a
  // refetch flag — hands every row the SAME style object, so the row memo can bail. A
  // fresh object per item per render was one of the two by-design-unstable row props.
  const slotStyles = useMemo(() => {
    const map = new Map<number, CSSProperties>()
    for (const item of virtualItems ?? []) {
      map.set(item.index, {
        position: 'absolute',
        top: 0,
        left: 0,
        // Span the FULL column width (not the viewport-wide `100%`) so a windowed row's
        // selection gradient / hover tint reaches every column under horizontal scroll;
        // `minWidth: 100%` still fills a narrow table.
        width: 'max-content',
        minWidth: '100%',
        // `item.start` bakes in the virtualizer's `scrollMargin` (header height), but the
        // row sits inside the body wrapper already below the in-flow header — subtract the
        // margin back out (it exists to fix `scrollToIndex`).
        transform: `translateY(${item.start - virtualizer.options.scrollMargin}px)`,
      })
    }

    return map
  }, [virtualItems, virtualizer])

  // The scroll skeleton (`showScrollSkeleton`): one tile drawn from the columns the row lays
  // out, over the stretches of the body above and below the mounted rows.
  //
  // The tile is MEMOIZED on the column geometry, as the pinned offsets are: its tracks are read
  // off the referentially-stable `table`, and this file is compiler-transformed, so without
  // the key a column the table gains or resizes would leave the skeleton drawing the old ones.
  const showsScrollSkeleton = isWindowed && showScrollSkeleton
  const skeletonRowHeightPx = DATA_TABLE_ROW_HEIGHT[activeDensity]
  const skeletonSlotPx = virtualizer.options.estimateSize(0)
  const scrollSkeletonTile = useMemo(
    () =>
      showsScrollSkeleton
        ? buildScrollSkeletonTile({
            tracks: getScrollSkeletonTracks(table, pinnedLeftInset, {
              columnDefs: preparedColumns,
              columnPinningState,
              columnSizingState,
              columnOrderState,
              columnVisibilityState,
            }),
            rowHeightPx: skeletonRowHeightPx,
            slotPx: skeletonSlotPx,
          })
        : null,
    [
      showsScrollSkeleton,
      table,
      pinnedLeftInset,
      preparedColumns,
      columnPinningState,
      columnSizingState,
      columnOrderState,
      columnVisibilityState,
      skeletonRowHeightPx,
      skeletonSlotPx,
    ],
  )
  const scrollSkeleton =
    scrollSkeletonTile === null
      ? null
      : {
          tile: scrollSkeletonTile,
          stretches: toUnmountedStretches(
            virtualItems ?? [],
            virtualizer.options.scrollMargin,
            virtualizer.getTotalSize(),
          ),
        }

  // The body's FINAL POSITION — the slot (windowed) or array index (in flow) whose occupant
  // sits on the card's bottom edge and must therefore not draw a trailing separator / gap.
  //
  // POSITION, not "is the dataset finished": the exception is a fact about the body's bottom
  // EDGE, and the virtualizer's slot count already encodes the frontier. Where `serverTotal` is
  // known, `resolveVirtualCount` appends `PLACEHOLDER_LOOKAHEAD_ROWS` while more pages remain, so
  // a table mid-fetch ends in a placeholder and no loaded row is last — without asking
  // `hasNextPage`. Where it is NOT known (`serverTotal: undefined`, and likewise on the
  // `MAX_SAFE_ELEMENT_HEIGHT_PX` clamp) the count collapses to the loaded rows with no lookahead,
  // and the last loaded row genuinely IS the bottom edge — so it suppresses, which is the one case
  // where this rule and a `hasNextPage` oracle disagree, and the oracle would double the card's
  // bottom line.
  //
  // Asking `hasNextPage` also gets the sparse dropped-duplicate case wrong in BOTH directions:
  // when the final raw offset is a dropped duplicate, its slot renders a PLACEHOLDER (see the
  // windowed body below), so the bottom edge is that placeholder while the last loaded row sits at
  // an interior slot and still needs its separator.
  //
  // Read LIVE off the virtualizer rather than recomputing `virtualCount ?? rows.length`: that is
  // exactly `useVirtualizer`'s own `count` argument (`use-data-table.ts`), and a hand-copy of it
  // here would be the same proxy-drift this fix exists to remove — every claim above (clamp-,
  // floor- and lookahead-safety, one expression for all four tiers) then holds by construction
  // instead of by two expressions happening to agree. Never DOM position: a windowed body mounts a
  // moving slice, so `:last-child` there is the bottom of the WINDOW (see `TableRow['last']`).
  const lastBodyIndex = virtualizer.options.count - 1

  const bodyRow = (row: Row<TData>, displayIndex: number, style?: CSSProperties): ReactNode => {
    // Per-row selection derived HERE so the row takes a boolean, not the model (see
    // `DataTableBodyRow`'s `selected` prop). The keepPreviousData guard rides along: a stale
    // row about to be replaced never renders CHECKED while the header — fed `loadedCount: 0`
    // — sits unchecked.
    const rowSelected =
      selectionModel !== undefined && !tableState.staleRows && isRowSelected(selectionModel, row.id)

    return (
      <DataTableBodyRow<TData>
        key={row.id}
        activeCellId={gridNav.activeDescendantId}
        ariaRowIndex={indexMap.getAriaRowIndex(displayIndex)}
        columnDefsIdentity={columns}
        density={activeDensity}
        detailPanel={detailPanel}
        detailPanelBaseId={detailPanelBaseId}
        displayIndex={displayIndex}
        divider={rowDivider}
        fitScrollport={usesGrowLayout}
        getRowProps={getRowProps}
        gridId={gridNav.gridId}
        // Logical index → the slot it occupies, so the comparison is in the SAME space as
        // `lastBodyIndex`. `logicalToVirtual` is identity under the dense count model (in-flow
        // and `virtualizeRows` alike) and the server-offset lookup under sparse, so this one
        // read is correct on every tier.
        lastRow={indexMap.logicalToVirtual(displayIndex) === lastBodyIndex}
        pinnedOffsets={pinnedOffsets}
        renderCell={renderCell}
        renderRow={renderRow}
        row={row}
        rowHighlightOnSelect={selection?.rowHighlight !== false}
        selectRowLabel={selection?.selectRowLabel}
        selected={rowSelected}
        selectionCellClassName={selectionColumn.selectionCellClassName}
        selectionPinnedStyle={selectionColumn.selectionPinnedStyle}
        style={style}
        surface={surface}
        onToggleRowSelection={selectionColumn.handleToggleRow}
      />
    )
  }

  // The summary bands, placed by `summaryRowPlacement` in the JSX below. SIBLINGS of the body,
  // never rows inside it: that is what keeps them out of the virtualized slots (and out of every
  // count) while they still scroll horizontally with the columns and hold their pinned-column
  // offsets. Each is offset by the bands between it and its edge, so a stack pins as a block.
  const summaryBandNodes = summaryEngineBands.map(({ band, key, row }, index) => (
    <DataTableSummaryBand
      key={key}
      density={activeDensity}
      divider={rowDivider}
      fitScrollport={usesGrowLayout}
      hasSelectionColumn={hasSelectionColumn}
      offset={summaryBandOffset(index)}
      pinnedOffsets={pinnedOffsets}
      row={row}
      rowProps={band.rowProps}
      // The BODY pinned style: the header's z-lift belongs to the header row alone.
      selectionPinnedStyle={selectionColumn.selectionPinnedStyle}
      surface={summaryBandSurface}
    />
  ))

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {(toolbar !== undefined || showMenu) && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">{toolbar}</div>
          {showMenu && (
            <VisibilityMenu
              columnVisibility={columnVisibilityState}
              getColumnLabel={getColumnLabel}
              table={table}
            />
          )}
        </div>
      )}

      {selectionToolbar}
      {selection?.renderSelectionBanner?.({
        header: selectionColumn.derivedSelection.header,
        selectedCountExact: selectionColumn.derivedSelection.selectedCountExact,
        serverTotal: infinite?.serverTotal,
        mode: (selectionModel ?? EMPTY_SELECTION).mode,
        bulkSelection: selectionColumn.derivedSelection.bulkSelection,
        selectAllMatching: selectionColumn.handleSelectAllMatching,
        clearSelection: selectionColumn.handleClearSelection,
      })}

      {/*
        `role="grid"` completes the WAI-ARIA grid graph the cells/rows declare and is the
        role on which `aria-busy` + `aria-activedescendant` are valid. `gridNav` wires U6
        keyboard nav here: the container (`tabIndex=0`) holds focus and drives the active cell
        via `aria-activedescendant`; each body cell carries the matching `id` + `data-active`.
        `onKeyDown`/`onFocus` act only on the container's OWN events, not those bubbling from a
        focused in-cell checkbox.
      */}
      {/*
        The CARD wraps BOTH the `role="grid"` scroll container and the body-replacing
        state overlay (a grid SIBLING — see the overlay comment below for why), so the
        sibling overlay renders inside the same rounded, clipped surface as the grid.
        `height` FILLS its bound; `maxHeight` caps it and lets short content hug (see both
        prop docs). Either one gives the grid a scroll viewport — `overflow-auto` roots get
        a zero automatic minimum as flex items, so the grid shrinks and scrolls inside the
        bounded card.
      */}
      <div
        className={dataTableCardVariants({ surface, radius: cardRadius })}
        style={hasBoundedHeight ? { height, maxHeight } : undefined}
      >
        <TableRoot
          ref={scrollRef}
          aria-activedescendant={gridNav.activeDescendantId}
          aria-busy={stateChrome.isBusy}
          aria-label={ariaLabel}
          aria-rowcount={indexMap.getAriaRowCount()}
          clipHorizontalOverflow={clipHorizontalOverflow}
          density={activeDensity}
          hideScrollbars={hideScrollbars}
          role="grid"
          style={gutterBandStyle}
          tabIndex={0}
          className={cn(
            // Grid fills the card while populated, shrinks to its header when an overlay
            // replaces the body. `min-h-0` lets this flex child shrink below content height
            // so `overflow-auto` scrolls, not grows.
            'min-h-0',
            tableState.kind === 'populated' ? 'flex-1' : 'flex-none',
          )}
          onFocus={gridNav.onFocus}
          onKeyDown={gridNav.onKeyDown}
        >
          {!hideHeader && (
            <DataTableHeaderRow
              columnMenuLabels={columnMenuLabels}
              columnSizingState={columnSizingState}
              density={activeHeaderDensity}
              divider={rowDivider}
              fitScrollport={usesGrowLayout}
              getColumnMove={getColumnMove}
              hasSelectionColumn={hasSelectionColumn}
              isAnyColumnResizing={isAnyColumnResizing}
              pinnedOffsets={pinnedOffsets}
              reorder={columnReorder}
              showColumnMenu={showColumnMenuResolved}
              sortingState={sortingState}
              surface={headerBandSurface}
              table={table}
              selectionCell={
                selection === undefined ? undefined : (
                  <TableSelectionHeaderCell
                    aria-label={selection.selectAllLabel}
                    checked={selectionColumn.derivedSelection.header.checked}
                    className={selectionColumn.selectionHeaderClassName}
                    colIndex={1}
                    indeterminate={selectionColumn.derivedSelection.header.indeterminate}
                    style={{
                      width: SELECTION_COLUMN_WIDTH_PX,
                      ...selectionColumn.selectionPinnedHeaderStyle,
                    }}
                    onCheckedChange={selectionColumn.handleToggleAll}
                  />
                )
              }
            />
          )}

          {/*
          TOP-placed summary bands: after the header, before the body, so they stick directly
          under the header and the rows scroll up beneath the whole chrome block.
        */}
          {isSummaryOnTop && summaryBandNodes}

          {/*
          BODY renders only when there are rows (`populated`, incl. the keepPreviousData
          window where stale rows stay mounted). In initialLoading/empty/error the overlay
          takes the body's place — which is why the initial load shows a skeleton, never an
          empty flash (the empty overlay is gated behind `isPending`).
        */}
          {tableState.kind === 'populated' &&
            (isWindowed ? (
              <TableBody
                className={headerBodyGapPx === 0 ? undefined : 'mt-2'}
                totalSize={virtualizer.getTotalSize()}
              >
                {scrollSkeleton === null
                  ? null
                  : scrollSkeleton.stretches.map((stretch) => (
                      <TableScrollSkeleton
                        key={stretch.key}
                        fromPx={stretch.fromPx}
                        tile={scrollSkeleton.tile}
                        toPx={stretch.toPx}
                      />
                    ))}
                {(virtualItems ?? []).map((item) => {
                  const slotStyle = slotStyles.get(item.index)

                  // Slot → logical row: identity in the dense model, server-offset
                  // reconciliation in sparse. A sparse slot past the loaded window resolves to
                  // `-1` → an aria-hidden placeholder holds it until its page loads. The
                  // logical index is the DISPLAY position, not the engine's `row.index`.
                  const logicalIndex = indexMap.virtualToLogical(item.index)
                  // Render each logical row EXACTLY ONCE, at its kept (first-occurrence) server
                  // offset. A dropped duplicate's own offset slot collapses to an already-rendered
                  // logical row (`logicalToServerOffset` points back to the FIRST occurrence, not
                  // this slot) — rendering it again would repeat the row and collide its React key
                  // (`row.id`), so that slot holds a placeholder instead. `rows` is the RAW engine
                  // model, so the kept offset indexes it directly.
                  const keptOffset =
                    logicalIndex < 0 ? -1 : indexMap.logicalToServerOffset(logicalIndex)
                  const row = keptOffset === item.index ? rows[keptOffset] : undefined
                  if (row === undefined) {
                    return (
                      <TablePlaceholderRow
                        key={`placeholder-${item.index}`}
                        density={activeDensity}
                        divider={rowDivider}
                        fitScrollport={usesGrowLayout}
                        // A placeholder occupies a real slot, so it can BE the body's bottom
                        // edge — the lookahead slots past the loaded frontier end the body on
                        // every scroll-to-bottom while pages remain, and a dropped duplicate at
                        // the final offset ends it permanently.
                        last={item.index === lastBodyIndex}
                        style={slotStyle}
                      />
                    )
                  }

                  return bodyRow(row, logicalIndex, slotStyle)
                })}
              </TableBody>
            ) : (
              <TableBody className={headerBodyGapPx === 0 ? undefined : 'mt-2'}>
                {rows.map((row, displayIndex) => bodyRow(row, displayIndex))}
              </TableBody>
            ))}

          {/*
          Bottom-of-list inline retry for a failed next-page fetch. Rendered INSIDE the scroll
          flow after the body — NOT an absolute overlay — so it never corrupts
          `getTotalSize()` / `aria-rowcount` / scroll math (those derive from the row model it
          sits outside of). `query.loadMoreError` stays inspectable.
        */}
          {tableState.kind === 'populated' && tableState.loadMoreError != null && (
            <LoadMoreErrorState
              copy={stateCopy}
              divider={rowDivider}
              onRetry={
                query?.retryLoadMore === undefined ? undefined : stateChrome.handleRetryLoadMore
              }
            />
          )}

          {/*
          BOTTOM-placed summary bands (the default). Last in the scroll flow — after the
          load-more retry — so at full scroll they reach their natural position BELOW everything
          instead of covering the retry they would otherwise stick over.
        */}
          {!isSummaryOnTop && summaryBandNodes}
        </TableRoot>

        {/*
          Non-blocking keepPreviousData refetch overlay — ABSOLUTE on the card, a sibling of
          the grid like the shadow rails below, parked under the sticky header. Not inside
          the scroll flow: rendered after the body its sticky position was the content's
          BOTTOM, so it only ever surfaced at full scroll (the invisible-sort-feedback bug).
        */}
        {stateChrome.refetchOverlay}

        {/*
          Continuous pinned-column shadow rails. Rendered on the CARD (this non-scrolling
          `relative` wrapper), NOT inside the scroll container, so each rail spans the full
          visible height as ONE element (continuous under vertical scroll) and stays glued to
          the pin edge under horizontal scroll — where a per-cell shadow restarted at every
          floating row. Only while the body exists (`populated`) and a side is actually pinned.
        */}
        {tableState.kind === 'populated' &&
          showPinnedShadows !== false &&
          (pinnedGutters.left > 0 || pinnedGutters.right > 0) && (
            <PinnedColumnShadows
              left={pinnedGutters.left}
              right={pinnedGutters.right}
              scrollbarWidth={overflowShadows.scrollbarWidth}
              showLeft={overflowShadows.start}
              showRight={overflowShadows.end}
            />
          )}

        {/*
          LEADING-EDGE SEAM GUARD: a 1px opaque lane at the scrollport's left edge, on the CARD
          (above the scroll root, which cannot help here — the scrolled content paints over the
          root's own background, see `stickyBandBackgroundStyle`).

          The defect: at a fractional device-pixel ratio — any browser zoom that is not 100%,
          or a scaled display — the leftmost sticky column's painted edge and the scrolled
          content's edge round to different device pixels, so a sub-pixel sliver of the column
          scrolling underneath shows to its left (QA round 15: reproduced at 0.9 DPR, invisible
          at 1.0/1.25/1.5). Only the LEADING edge needs this: every other pinned boundary abuts
          another opaque pinned cell.

          The lane repaints exactly what the sticky cells beside it paint, so it is invisible in
          every state: the CHROME bands' surface at top/bottom (`stickyBandBackgroundStyle`, the
          same layered-gradient helper the scrollbar gutter uses) over the body's surface. Inset
          from the bottom by the horizontal scrollbar so it never paints over the bar's left end,
          exactly as the shadow rails are.
        */}
        {tableState.kind === 'populated' && (hasSelectionColumn || pinnedGutters.left > 0) && (
          <div
            className="pointer-events-none absolute left-0 top-0 z-[4] w-[1px]"
            style={{
              bottom: bottomShadow.scrollbarHeight,
              backgroundColor: SURFACE_COLOR[surface],
              ...gutterBandStyle,
            }}
          />
        )}

        {/*
          Each summary band's LEADING overlay (`summaryRow.leading`) — on the CARD like the
          rails, so it stays put under horizontal scroll, at its band's own height and offset so
          it can never drift from the band vertically (the hands "N hands" readout previously
          anchored to an OUTER container and detached whenever the card hugged short content).
          A bottom band's offset is scrollbar-aware; a top band's is not, as no scrollbar runs
          along the top edge. It paints the band's own face (surface, body-facing edge, rule
          colour), so a consumer supplies content only and cannot drift from the band. A top
          band's overlay sits beside the header, never over it.
        */}
        {summaryEngineBands.map(({ band, key }, index) => {
          if (band.leading === undefined) {
            return null
          }
          const offset = summaryBandOffset(index)

          return (
            <div
              key={key}
              className={dataTableSummaryLeadingVariants({
                surface: summaryBandSurface,
                placement: 'top' in offset ? 'top' : 'bottom',
                divider: rowDivider,
              })}
              style={{
                ...('top' in offset
                  ? { top: offset.top }
                  : { bottom: bottomShadow.scrollbarHeight + offset.bottom }),
                height: summaryRowHeightPx,
              }}
            >
              {band.leading}
            </div>
          )
        })}

        {/*
          Bottom "more rows below" shadow. Also on the CARD (non-scrolling wrapper), not a mask
          on the scroll container — a mask hides scrollbars in Chromium (why the old fade was
          dropped). Only while the body exists (`populated`); the overlay states have no
          scrollable body. Inset by the scrollbar dimensions so it never darkens either bar.
        */}
        {tableState.kind === 'populated' && (
          <BottomOverflowShadow
            // Above a bottom-pinned summary band, never over it: the band is not what scrolls,
            // so fading it would read as a rendering artefact rather than "more rows below". A
            // top-placed stack leaves the bottom edge to the rows.
            bottomInset={isSummaryOnTop ? 0 : summaryBandHeightPx}
            scrollbarHeight={bottomShadow.scrollbarHeight}
            scrollbarWidth={overflowShadows.scrollbarWidth}
            show={bottomShadow.show}
            surface={surface}
          />
        )}

        {/*
          The inline-END counterpart, opt-in (see `showEndOverflowShadow`). Skipped when
          columns are pinned right: `PinnedColumnShadows` already owns that edge, and a surface
          fade over a pinned shadow would fight it on the same boundary.
        */}
        {tableState.kind === 'populated' &&
          showEndOverflowShadow === true &&
          pinnedGutters.right === 0 && (
            <EndOverflowShadow
              bands={stickyBands}
              scrollbarHeight={bottomShadow.scrollbarHeight}
              scrollbarWidth={overflowShadows.scrollbarWidth}
              show={overflowShadows.end}
              surface={surface}
            />
          )}

        {/*
          The body-replacing overlay (skeleton, empty, error, or a consumer `renderState`). A
          DOM SIBLING of the grid, NOT a child: its error-state Retry `<button>` nested under
          `role="grid"` would orphan it as a non-row child (`aria-required-children`). As the
          grid's sibling inside the card it fills the body region — `flex-1` claims the space
          the header-only grid leaves, `[&>*]:flex-1` stretches the chosen overlay. The grid
          keeps only its header row, so the role graph stays valid.
        */}
        {stateChrome.standaloneOverlay !== null && (
          <div className="flex min-h-0 flex-1 flex-col overflow-auto [&>*]:flex-1">
            {stateChrome.standaloneOverlay}
          </div>
        )}
      </div>

      {/*
        The `aria-live` status + alert regions (U9). `aria-busy` carries no message and is
        announced unreliably, so it is PAIRED with these regions — the region announces,
        `aria-busy` is the state flag.
      */}
      <TableLiveRegion
        assertive={stateChrome.liveMessages.assertive}
        polite={stateChrome.liveMessages.polite}
      />

      {children?.(table, virtualizer)}
    </div>
  )
}
